import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";
import {
  CookedEvent,
  CookEvictionReason,
  CookOps,
  CookOven as CookOvenData,
  CookRecipe,
  CookSkill,
  CookState,
  cookableItemIds,
  findRecipeClient,
  joinCook,
} from "../api/cook";
import { ActivityEvictionPage } from "../components/ActivityEvictionPage";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { CookInventoryModal } from "../components/CookInventoryModal";
import { CookOven } from "../components/CookOven";
import { CookSidePanel } from "../components/CookSidePanel";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { playSfx } from "../logic/sfx";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { useAnnouncer } from "../logic/LiveAnnouncer";

interface Props {
  title: string;
  breadcrumb: string | null;
  parentPath: string;
}

// All six ovens are the same model — "Brick Oven". Different tiers
// of oven are a future-content axis (better recipes, faster cooks,
// etc.); for now the unlock progression alone gates access.
const OVEN_NAME = "BRICK OVEN";

export function CookPage({ title, breadcrumb, parentPath }: Props) {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const { narrow } = useResponsiveLayout();

  const [user, setUser] = useState<api.User | null>(null);
  const [inventory, setInventory] = useState<api.InventoryResponse | null>(
    null,
  );
  const [ovens, setOvens] = useState<CookOvenData[]>([]);
  const [skill, setSkill] = useState<CookSkill | null>(null);
  const [recipes, setRecipes] = useState<CookRecipe[]>([]);
  const [sessionXp, setSessionXp] = useState(0);
  const [sessionCooked, setSessionCooked] = useState<Map<string, number>>(
    new Map(),
  );
  const [picker, setPicker] = useState<{
    ovenIdx: number;
    slotIdx: number;
  } | null>(null);
  const [eviction, setEviction] = useState<CookEvictionReason | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const opsRef = useRef<CookOps | null>(null);
  const announce = useAnnouncer();

  // ---- /api/me + initial inventory ----
  useEffect(() => {
    let cancelled = false;
    void Promise.all([api.getMe(), api.getInventory()]).then(
      ([me, inv]) => {
        if (cancelled) return;
        setUser(me.user);
        setInventory(inv);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- channel join ----
  useEffect(() => {
    let cancelled = false;
    let leave: (() => void) | null = null;

    void (async () => {
      const me = await api.getMe();
      if (cancelled) return;

      try {
        const ops = await joinCook(me.user.id, {
          onState: (s: CookState) => {
            if (cancelled) return;
            setOvens(s.ovens);
            setSkill(s.skill);
            setRecipes(s.recipes);
          },
          onOvenChanged: (oven: CookOvenData) => {
            if (cancelled) return;
            setOvens((prev) =>
              prev.map((o) => (o.idx === oven.idx ? oven : o)),
            );
          },
          onCooked: (e: CookedEvent) => {
            if (cancelled) return;
            // Sizzle is the "food finished baking" cue. XP credit
            // and the session-cooked tally both wait for COLLECT —
            // the item isn't in the player's hands until they take
            // it, and the reward shouldn't fire until then either.
            playSfx("food_finished_sizzle");
            announce(`Oven ${e.oven_idx + 1} ready to collect`);
          },
          onEvicted: (reason) => {
            if (cancelled) return;
            setEviction(reason);
          },
          onError: (msg) => {
            if (cancelled) return;
            setJoinError(msg);
          },
        });

        opsRef.current = ops;
        leave = () => {
          ops.channel.leave();
        };
      } catch {
        // join error already surfaced via onError
      }
    })();

    return () => {
      cancelled = true;
      if (leave) leave();
      opsRef.current = null;
    };
  }, []);

  const refreshInventory = useCallback(async () => {
    const inv = await api.getInventory();
    setInventory(inv);
  }, []);

  const showError = useCallback((reason: string) => {
    const messages: Record<string, string> = {
      no_coal: "No coal in inventory.",
      oven_locked: "This oven is locked.",
      cannot_light: "The oven can't be lit right now.",
      unlit: "Light the oven first.",
      cooking_in_progress: "Already cooking.",
      output_blocked: "Collect the output first.",
      not_enough_fuel: "Not enough fuel.",
      no_recipe: "No recipe matches those ingredients.",
      bad_slot: "Bad slot.",
      slot_occupied: "Slot is already occupied.",
      slot_empty: "Slot is empty.",
      not_food: "That isn't food.",
      not_in_inventory: "You don't have that item.",
      no_output: "Nothing to collect.",
      no_such_oven: "Oven not found.",
      unknown_item: "Unknown item.",
    };
    setErrorBanner(messages[reason] ?? `Cooking error: ${reason}`);
    setTimeout(() => setErrorBanner(null), 1800);
  }, []);

  const itemNames = useMemo(() => {
    const m = new Map<string, string>();
    inventory?.items.forEach((i) => m.set(i.id, i.name));
    return m;
  }, [inventory]);

  const cookableIds = useMemo(() => cookableItemIds(recipes), [recipes]);

  const hasCoal = useMemo(() => {
    if (!inventory) return false;
    return inventory.slots.some(
      (s) => s.item_id === "coal" && s.count > 0,
    );
  }, [inventory]);

  const handleIgnite = useCallback(
    async (ovenIdx: number) => {
      if (!opsRef.current) return;
      playSfx("ai_pop");
      const res = await opsRef.current.light(ovenIdx);
      if (!res.ok) {
        showError(res.reason);
        return;
      }
      playSfx("furnace_lit");
      await refreshInventory();
    },
    [refreshInventory, showError],
  );

  const handleSlotClick = useCallback(
    async (ovenIdx: number, slotIdx: number) => {
      if (!opsRef.current) return;
      const oven = ovens.find((o) => o.idx === ovenIdx);
      if (!oven) return;
      const current = oven.slots[slotIdx];
      if (current != null) {
        // Clicking an occupied slot clears it back to inventory.
        // Sound matches the foraging pop / take cue.
        playSfx("ai_pop");
        const res = await opsRef.current.clear(ovenIdx, slotIdx);
        if (!res.ok) {
          showError(res.reason);
          return;
        }
        await refreshInventory();
      } else {
        setPicker({ ovenIdx, slotIdx });
      }
    },
    [ovens, refreshInventory, showError],
  );

  const handlePickItem = useCallback(
    async (itemId: string) => {
      if (!opsRef.current || !picker) return;
      const { ovenIdx, slotIdx } = picker;
      setPicker(null);
      // Placing an item is silent — no sound.
      const res = await opsRef.current.place(ovenIdx, slotIdx, itemId);
      if (!res.ok) {
        showError(res.reason);
        return;
      }
      await refreshInventory();
    },
    [picker, refreshInventory, showError],
  );

  const handleCook = useCallback(
    async (ovenIdx: number) => {
      if (!opsRef.current) return;
      const res = await opsRef.current.cook(ovenIdx);
      if (!res.ok) {
        showError(res.reason);
      }
    },
    [showError],
  );

  const handleCollect = useCallback(
    async (ovenIdx: number) => {
      if (!opsRef.current) return;
      playSfx("ai_pop");
      const res = await opsRef.current.collect(ovenIdx);
      if (!res.ok) {
        showError(res.reason);
        return;
      }
      // Collect is the reward moment: XP, the level-up SFX, the
      // session XP bump, the session-cooked tally — everything
      // fires together when the food actually lands in the player's
      // bag.
      const { collected, xp_gained, leveled_up, skill: newSkill } = res.payload;
      setSkill(newSkill);
      setSessionXp((x) => x + xp_gained);
      if (leveled_up) playSfx("ai_level_up");
      setSessionCooked((prev) => {
        const next = new Map(prev);
        next.set(collected, (next.get(collected) ?? 0) + 1);
        return next;
      });
      await refreshInventory();
    },
    [refreshInventory, showError],
  );

  if (eviction) {
    return (
      <ActivityEvictionPage
        activityLabel="Cooking"
        subtitle="The kitchen stays as you left it. Any ingredients in oven slots and any uncollected output are waiting for you on your next visit."
        reason={eviction}
        title={title}
        breadcrumb={breadcrumb}
        parentPath={parentPath}
        user={user}
      />
    );
  }

  const cookingLevel = skill?.level ?? 1;

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          display: "grid",
          gap: 16,
        }}
      >
        <AppHeader
          user={user}
          backTo={parentPath}
          backLabel="◀ BACK"
        />
        <Breadcrumb text={breadcrumb} />

        {joinError && (
          <div
            style={{
              padding: 10,
              border: `2px solid ${E32.scarlet}`,
              color: E32.scarlet,
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
            }}
          >
            {joinError === "in_battle"
              ? "Finish your battle before cooking."
              : `Couldn't enter the kitchens: ${joinError}`}
            <button
              onClick={() => navigate(parentPath)}
              style={{
                marginLeft: 12,
                background: "transparent",
                color: E32.scarlet,
                border: `1px solid ${E32.scarlet}`,
                padding: "2px 8px",
                cursor: "pointer",
                fontFamily: theme.fontDisplay,
                fontSize: 10,
                letterSpacing: theme.letterSpacing,
              }}
            >
              GO BACK
            </button>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: narrow
              ? "minmax(0, 1fr)"
              : "minmax(0, 1fr) 300px",
            gap: 16,
            alignItems: "start",
          }}
        >
          {/* Global cooking-page keyframes — flicker glow, state-blink
              badge, slot hover highlight. Mounted once at page level. */}
          <style>{`
            @keyframes cook-flicker {
              0%   { filter: brightness(1.0); }
              50%  { filter: brightness(1.12) hue-rotate(-4deg); }
              100% { filter: brightness(0.96); }
            }
            @keyframes cook-state-blink {
              0%, 49% { opacity: 1; }
              50%, 100% { opacity: 0.45; }
            }
            .cook-hearth-lit {
              animation: cook-flicker 220ms steps(2, end) infinite;
            }
            .cook-state-blink {
              animation: cook-state-blink 1000ms steps(2, end) infinite;
            }
            .cook-slot-empty:hover {
              box-shadow: inset 0 0 0 2px ${E32.butter};
              border-color: ${E32.butter} !important;
            }
          `}</style>
          <section
            style={{
              position: "relative",
              backgroundImage: `url('/cooking/background_tile.png')`,
              backgroundRepeat: "repeat",
              // 2× the native tile size, per the user spec.
              backgroundSize: "128px 128px",
              backgroundColor: E32.oxblood,
              imageRendering: "pixelated",
              border: `2px solid ${theme.panelEdgeStrong}`,
              // 16px on narrow screens (more room for the inner grid),
              // 28px on wide. Drops the inner gap consistently.
              padding: narrow ? 16 : 28,
              // Fixed px floor (no viewport units allowed) so the
              // hearth stays a generous panel; the page scrolls past
              // it when content runs long.
              minHeight: 480,
            }}
          >
            {errorBanner && (
              <div
                style={{
                  position: "absolute",
                  top: 10,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 300,
                  padding: "6px 14px",
                  background: E32.scarlet,
                  color: E32.parchment,
                  fontFamily: theme.fontDisplay,
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  border: `2px solid ${E32.ink}`,
                }}
              >
                {errorBanner}
              </div>
            )}

            <div
              style={{
                display: "grid",
                // Auto-fit grid: pack as many oven columns as fit at
                // a 280px minimum each, then wrap. Below ~600px the
                // grid naturally collapses to a single column without
                // any media queries. Above ~880px (after the 300px
                // side panel takes its slice and the kitchen padding
                // is accounted for) we get the design's 2-column
                // layout.
                // 360px minimum — gives a comfortable 2-column layout
                // at the design's target wide width and only flips to
                // a single column below ~770px (kitchen interior). The
                // design called out a 2-col grid, not auto-overflow.
                gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
                gap: 16,
              }}
            >
              {ovens.map((oven) => (
                <CookOven
                  key={oven.idx}
                  oven={oven}
                  name={OVEN_NAME}
                  hasCoal={hasCoal}
                  match={findRecipeClient(oven.slots, recipes, cookingLevel)}
                  itemNames={itemNames}
                  onIgnite={() => handleIgnite(oven.idx)}
                  onSlotClick={(slotIdx) =>
                    handleSlotClick(oven.idx, slotIdx)
                  }
                  onCook={() => handleCook(oven.idx)}
                  onCollect={() => handleCollect(oven.idx)}
                />
              ))}
            </div>
          </section>

          <CookSidePanel
            skill={skill}
            sessionXp={sessionXp}
            sessionCooked={sessionCooked}
            itemNames={itemNames}
          />
        </div>
      </div>

      {picker && inventory && (
        <CookInventoryModal
          slots={inventory.slots}
          items={inventory.items}
          cookableIds={cookableIds}
          onPick={handlePickItem}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}
